import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Crown, Plus, PencilSimple, Users, Check, X } from "@phosphor-icons/react";
import { api, formatBRL } from "@/lib/gado";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const EMPTY_FORM = {
  name: "",
  slug: "",
  description: "",
  price_month: "",
  price_year: "",
  pdfs_mes: "",
  analises_ia_mes: "",
  relatorio_mensal: false,
  active: true,
  highlight: false,
};

const num = (v) => (v === "" || v == null ? null : parseFloat(String(v).replace(",", ".")));
const intOrNull = (v) => (v === "" || v == null ? null : parseInt(v, 10));

function planToForm(p) {
  return {
    name: p.name || "",
    slug: p.slug || "",
    description: p.description || "",
    price_month: p.price_month ?? "",
    price_year: p.price_year ?? "",
    pdfs_mes: p.limits?.pdfs_mes ?? "",
    analises_ia_mes: p.limits?.analises_ia_mes ?? "",
    relatorio_mensal: !!p.limits?.relatorio_mensal,
    active: p.active !== false,
    highlight: !!p.highlight,
  };
}

function LimitLine({ label, value }) {
  return (
    <p className="flex justify-between text-xs py-1 border-b border-gado-line/50 last:border-0">
      <span className="text-gado-muted">{label}</span>
      <b className="text-gado-ink">{value == null ? "Ilimitado" : value}</b>
    </p>
  );
}

export default function AdminPlans() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // { plan_id?, ...form }
  const isNew = editing && !editing.plan_id;

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: async () => (await api.get("/admin/plans")).data,
  });

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Informe o nome do plano");
    if (isNew && !editing.slug.trim()) return toast.error("Informe o identificador (slug)");
    const current = plans.find((p) => p.plan_id === editing.plan_id);
    if (!isNew && !editing.active && current?.active && (current?.users_count || 0) > 0) {
      if (!window.confirm(`O plano "${current.name}" tem ${current.users_count} usuário(s). Desativar mesmo assim?`)) return;
    }
    const payload = {
      name: editing.name.trim(),
      slug: editing.slug.trim().toLowerCase(),
      description: editing.description,
      price_month: num(editing.price_month) ?? 0,
      price_year: num(editing.price_year),
      limits: {
        pdfs_mes: intOrNull(editing.pdfs_mes),
        analises_ia_mes: intOrNull(editing.analises_ia_mes),
        relatorio_mensal: editing.relatorio_mensal,
      },
      active: editing.active,
      highlight: editing.highlight,
      sort_order: editing.sort_order ?? 0,
    };
    try {
      if (isNew) {
        await api.post("/admin/plans", payload);
        toast.success("Plano criado");
      } else {
        await api.put(`/admin/plans/${editing.plan_id}`, payload);
        toast.success("Plano atualizado");
      }
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar plano");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-plans-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Planos</h1>
          <p className="text-gado-muted text-sm">Planos de assinatura, preços e limites de uso.</p>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY_FORM })}
          data-testid="new-plan-button"
          className="bg-gado-green hover:bg-gado-greenHover text-white shrink-0"
        >
          <Plus size={16} className="mr-1" /> Novo plano
        </Button>
      </div>

      {isLoading && <p className="text-sm text-gado-muted text-center py-8">Carregando…</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="plans-grid">
        {plans.map((p) => (
          <div
            key={p.plan_id}
            className={`rounded-xl border p-4 bg-white min-w-0 ${
              p.highlight ? "border-gado-gold/50 shadow-md shadow-gado-gold/10" : "border-gado-line"
            } ${!p.active ? "opacity-60" : ""}`}
            data-testid={`plan-card-${p.slug}`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <Crown size={17} weight="fill" className={p.slug === "pro" ? "text-gado-gold" : "text-gado-muted"} />
                <p className="font-cabinet font-bold text-gado-ink truncate">{p.name}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!p.active && <Badge variant="outline" className="text-[10px] border-gado-line text-gado-muted">Inativo</Badge>}
                {p.highlight && <Badge variant="outline" className="text-[10px] border-gado-gold/40 bg-gado-gold/10 text-gado-gold">Destaque</Badge>}
              </div>
            </div>
            <p className="text-xs text-gado-muted mb-3 truncate">{p.description || "—"}</p>

            <div className="flex items-baseline gap-1 mb-3">
              <span className="font-cabinet text-2xl font-extrabold text-gado-ink tabular-nums">{formatBRL(p.price_month)}</span>
              <span className="text-xs text-gado-muted">/mês</span>
              {p.price_year != null && (
                <span className="text-xs text-gado-muted ml-2 tabular-nums">ou {formatBRL(p.price_year)}/ano</span>
              )}
            </div>

            <div className="rounded-lg bg-gado-bg/60 px-3 py-1.5 mb-3">
              <LimitLine label="PDFs por mês" value={p.limits?.pdfs_mes} />
              <LimitLine label="Análises de IA por mês" value={p.limits?.analises_ia_mes} />
              <p className="flex justify-between text-xs py-1">
                <span className="text-gado-muted">Relatório mensal</span>
                {p.limits?.relatorio_mensal
                  ? <Check size={14} weight="bold" className="text-gado-green" />
                  : <X size={14} weight="bold" className="text-gado-line" />}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-gado-muted inline-flex items-center gap-1">
                <Users size={13} /> {p.users_count} usuário(s)
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing({ plan_id: p.plan_id, slug: p.slug, sort_order: p.sort_order, ...planToForm(p) })}
                data-testid={`edit-plan-${p.slug}`}
              >
                <PencilSimple size={14} className="mr-1" /> Editar
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Dialog criar/editar */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle className="text-gado-ink">{isNew ? "Novo plano" : `Editar ${editing?.name}`}</DialogTitle>
            <DialogDescription className="text-xs">
              Limites vazios = ilimitado. O identificador (slug) não pode mudar depois de criado.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gado-muted">Nome</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1" data-testid="plan-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gado-muted">Identificador (slug)</Label>
                  <Input
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    disabled={!isNew}
                    placeholder="ex: pro-plus"
                    className="mt-1 disabled:opacity-60"
                    data-testid="plan-slug-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gado-muted">Descrição</Label>
                <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1" data-testid="plan-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gado-muted">Preço mensal (R$)</Label>
                  <Input value={editing.price_month} inputMode="decimal" onChange={(e) => setEditing({ ...editing, price_month: e.target.value.replace(/[^0-9.,]/g, "") })} className="mt-1 font-mono" data-testid="plan-price-month" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gado-muted">Preço anual (R$)</Label>
                  <Input value={editing.price_year} inputMode="decimal" onChange={(e) => setEditing({ ...editing, price_year: e.target.value.replace(/[^0-9.,]/g, "") })} className="mt-1 font-mono" data-testid="plan-price-year" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gado-muted">PDFs/mês</Label>
                  <Input value={editing.pdfs_mes} inputMode="numeric" placeholder="Ilimitado" onChange={(e) => setEditing({ ...editing, pdfs_mes: e.target.value.replace(/[^0-9]/g, "") })} className="mt-1 font-mono" data-testid="plan-limit-pdfs" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gado-muted">Análises IA/mês</Label>
                  <Input value={editing.analises_ia_mes} inputMode="numeric" placeholder="Ilimitado" onChange={(e) => setEditing({ ...editing, analises_ia_mes: e.target.value.replace(/[^0-9]/g, "") })} className="mt-1 font-mono" data-testid="plan-limit-ia" />
                </div>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
                <label className="flex items-center gap-2 text-sm text-gado-ink cursor-pointer">
                  <Switch checked={editing.relatorio_mensal} onCheckedChange={(v) => setEditing({ ...editing, relatorio_mensal: v })} data-testid="plan-flag-report" />
                  Relatório mensal
                </label>
                <label className="flex items-center gap-2 text-sm text-gado-ink cursor-pointer">
                  <Switch checked={editing.highlight} onCheckedChange={(v) => setEditing({ ...editing, highlight: v })} data-testid="plan-flag-highlight" />
                  Destaque
                </label>
                <label className="flex items-center gap-2 text-sm text-gado-ink cursor-pointer">
                  <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="plan-flag-active" />
                  Ativo
                </label>
              </div>
              <Button onClick={save} className="w-full bg-gado-green hover:bg-gado-greenHover text-white" data-testid="plan-save-button">
                {isNew ? "Criar plano" : "Salvar alterações"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
